/**
 * Time-phased execution planning — Supabase I/O and orchestration.
 * Bridges viability planning outputs → calendar placements.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  getScenarios,
  getPriorities,
  getCapacityRows,
  getPriorityCapacityRows,
  getTeamParticipation,
  getWorkUnits,
  getDependencies,
  getPriorityDependencies,
  createCapacityRow,
  createTeamParticipation,
  updateTeamParticipation,
  createPriority,
  updatePriority,
  upsertPriorityOverride,
  createScenario,
  activateScenario,
  bindScenarioInputSource,
  duplicateScenario,
  runEngine,
  runEngineScoped,
  upsertPlanningRun,
  updateScenario,
  createPriorityDependency,
  type EngineInputSource,
} from './planningEngineService';
import {
  deriveTimePhasedAssumptions,
  mergePreservedAssumptionStatus,
  type TimePhasedAssumptionStatus,
  type TimePhasedAssumptionsSnapshot,
} from './timePhasedAssumptions';
import {
  derivePredictability,
  resolveLaunchFlexibility,
} from './calendarPredictability';
import {
  parseAnchoringFromNotes,
  upsertAnchoringInNotes,
  type PlacementAnchoring,
} from './placementAnchoring';
import { invalidateShadowImpactCache } from './assumptionShadowImpact';
import { assumptionsFor, loadPatternContext } from './typePatternsService';
import { fetchHeldValues } from './itemCycleStandingService';
import { HOURS_PER_CU, predictabilityFromAssumptions, type SourcedWorkloadLine } from './typePatterns';
import {
  getWorkspacePredictabilityProfiles,
  toDerivePredictabilityWorkspaceProfiles,
} from './predictabilityProfilesService';
import { getCurrentPorSnapshot, getLatestPlanVersion } from './planningService';
import type { AssumptionStatus } from '../types/planning';
import { runPlanningEngine } from './planningEngineModule';
import { getCurrentWorkspaceId } from './supabaseSync';
import { getRootQueueIdForWorkspace } from './queueRouting';
import { reconcileItemPoolMemberships } from './itemPoolMembershipSync';
import { dispatchPrioritizationDashboardRefresh } from './prioritizationDashboardRefreshEvents';
import { useUserStore } from '../stores/prioritization/userStore';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import type { Item } from '../types/prioritization/lifecycle';
import type { Priority } from './planningEngineModule.types';
import { generateTimeBuckets } from './planningEngine';
import {
  runTimePhasedPlacementEngine,
  applyPromoteLever,
  applyDelayLever,
} from './timePhasedPlanningEngine';
import type {
  BucketUtilization,
  CalendarChangeSet,
  LaunchProfile,
  LaunchProfilePhase,
  SupplyCalendarRow,
  TimePhasedEngineInput,
  TimePhasedEngineOutput,
  TimePhasedPlacement,
  TimePhasedPriorityInput,
  PortfolioRollupSource,
  ScenarioDiffEntry,
} from './timePhasedPlanning.types';
import {
  DEFAULT_LAUNCH_PROFILES as BUILTIN_PROFILES,
  SHARED_TEAM_RESOURCE_TYPES as TEAMS,
  canonicalizeResourceType,
  resolveSupplyTeamCatalog,
} from './timePhasedPlanning.types';
import { HOURS_PER_CAPACITY_UNIT } from './itemToPlanningAdapter';
import { resolveInputItemIds } from './planningService';
import type { Scenario } from './planningEngineModule.types';
import {
  buildCalendarChangeSet,
  coerceChangeSet,
} from './calendarChangeSet';
import {
  fillPeriodCommitmentsFromWindow,
  participationUsesPeriodGrain,
  periodsCoverLaunchWindow,
  staleConfirmedPeriods,
  rollupRequestStatus,
} from './participationPeriod';
import {
  copyFundingEnvelopesToScenario,
  ensureScenarioFinancialLayer,
  getCostPlansForScenario,
  getFundingEnvelopes,
  getLatestFinancialUtilization,
  persistFinancialSnapshot,
  runFinancialForPlacements,
  seedDemoFinancialLayer,
} from './timePhasedFinancialService';
import type { TimePhasedFinancialOutput } from './timePhasedFinancial.types';
import { runTimePhasedFinancialEngine } from './timePhasedFinancialEngine';

export { BUILTIN_PROFILES, TEAMS, resolveSupplyTeamCatalog, ensureScenarioFinancialLayer };

/**
 * Persist a live-scope planning_runs instance from DB priorities/WUs so Above/Below
 * matches the calendar portfolio after Keith/Sample seed (without requiring a UI Run).
 */
async function persistLiveScopeViabilityInstance(
  projectId: string,
  scenarioId: string,
): Promise<void> {
  try {
    const viability = await runEngine(projectId, scenarioId);
    await upsertPlanningRun({
      projectId,
      scenarioId,
      engineInput: { type: 'live' },
      scopedItemIds: null,
      output: viability,
    });
  } catch (err) {
    console.warn('[timePhased] failed to persist viability instance after seed', err);
  }
}

/** Shared GTM teams that gate launchability when participation is required. */
const LAUNCH_GATE_TEAMS = new Set([
  'marketing',
  'legal',
  'design',
  'support',
  'finance',
  'product_marketing',
  'customer_support',
  'ux',
]);

function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function toLaunchProfile(row: Record<string, unknown>): LaunchProfile {
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    phases: (row.payload as LaunchProfilePhase[]) ?? [],
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function toSupplyRow(row: Record<string, unknown>): SupplyCalendarRow {
  return {
    id: row.id as string,
    scenarioId: row.scenario_id as string,
    resourceType: row.resource_type as string,
    resourceName: row.resource_name as string | undefined,
    bucketStart: row.bucket_start as string,
    bucketEnd: row.bucket_end as string,
    supplyHours: Number(row.supply_hours),
  };
}

function toPlacement(row: Record<string, unknown>): TimePhasedPlacement {
  const rationale = (row.placement_rationale as Record<string, unknown>) ?? {};
  return {
    priorityId: row.priority_id as string,
    priorityName: (rationale.priorityName as string) ?? '',
    owningOrg: (rationale.owningOrg as string) || undefined,
    feasibleStart: (row.feasible_start as string) ?? undefined,
    feasibleEnd: (row.feasible_end as string) ?? undefined,
    targetLaunchStart: (row.target_launch_start as string) ?? undefined,
    targetLaunchEnd: (row.target_launch_end as string) ?? undefined,
    slipWeeks: row.slip_weeks != null ? Number(row.slip_weeks) : undefined,
    placementStatus: row.placement_status as TimePhasedPlacement['placementStatus'],
    buildable: Boolean(row.buildable),
    launchable: Boolean(row.launchable),
    assumedProfile: Boolean(rationale.assumedProfile),
    rationale,
  };
}

export function defaultHorizonFromScenario(planningWeeks: number): { start: string; end: string } {
  const start = new Date();
  const startIso = start.toISOString().slice(0, 10);
  const end = new Date(start);
  end.setDate(end.getDate() + planningWeeks * 7);
  return { start: startIso, end: end.toISOString().slice(0, 10) };
}

async function getWorkspaceDefaultWeeklyHours(projectId: string): Promise<number> {
  try {
    const { data } = await getSupabaseClient()
      .from('planning_workspace_defaults')
      .select('weekly_capacity_hours')
      .eq('workspace_id', projectId)
      .maybeSingle();
    const hours = Number(data?.weekly_capacity_hours);
    return Number.isFinite(hours) && hours > 0 ? hours : 40;
  } catch {
    return 40;
  }
}

/**
 * Seed default supply calendar from viability `planning_capacity` rows
 * (hours/week per resource type), falling back to Planning Defaults weekly
 * hours × Settings Resource Types catalog. Never invents parallel team keys.
 */
export async function ensureDefaultSupplyCalendar(
  scenarioId: string,
  horizonStart: string,
  horizonEnd: string,
  hoursPerWeek?: number,
  projectId?: string,
): Promise<SupplyCalendarRow[]> {
  const sb = getSupabaseClient();
  const { data: existing } = await sb
    .from('planning_supply_calendar')
    .select('*')
    .eq('scenario_id', scenarioId);
  if (existing && existing.length > 0) {
    return existing.map((r) => toSupplyRow(r as Record<string, unknown>));
  }

  const buckets = generateTimeBuckets(horizonStart, horizonEnd, 'week');
  const defaultHours =
    hoursPerWeek ??
    (projectId ? await getWorkspaceDefaultWeeklyHours(projectId) : 40);

  // Prefer capacity model rows for this scenario.
  let teams: { type: string; label: string; hours: number }[] = [];
  if (projectId) {
    try {
      const capacity = await getCapacityRows(projectId, scenarioId);
      if (capacity.length > 0) {
        const byType = new Map<string, { label: string; hours: number }>();
        for (const row of capacity) {
          const type = canonicalizeResourceType(row.resourceType);
          const prev = byType.get(type);
          const hours = row.weeklyCapacityHours > 0 ? row.weeklyCapacityHours : defaultHours;
          if (!prev) {
            byType.set(type, { label: row.resourceName || type, hours });
          } else {
            // Sum multiple named resources of the same type.
            byType.set(type, {
              label: prev.label,
              hours: prev.hours + hours,
            });
          }
        }
        teams = [...byType.entries()].map(([type, v]) => ({
          type,
          label: v.label,
          hours: v.hours,
        }));
      }
    } catch {
      // Capacity table may be empty / unavailable — fall through.
    }
  }

  if (teams.length === 0) {
    teams = resolveSupplyTeamCatalog().map((t) => ({
      type: t.type,
      label: t.label,
      hours: defaultHours,
    }));
  }

  const rows = teams.flatMap((team) =>
    buckets.map((b) => ({
      scenario_id: scenarioId,
      resource_type: team.type,
      resource_name: team.label,
      bucket_start: b.time_bucket_start,
      bucket_end: b.time_bucket_end,
      supply_hours: team.hours,
    })),
  );

  const { data, error } = await sb.from('planning_supply_calendar').insert(rows).select();
  if (error) throw error;
  return (data ?? []).map((r) => toSupplyRow(r as Record<string, unknown>));
}

export async function getLaunchProfiles(projectId: string): Promise<LaunchProfile[]> {
  const { data, error } = await getSupabaseClient()
    .from('launch_profiles')
    .select('*')
    .eq('project_id', projectId)
    .order('name');
  if (error) throw error;
  return (data ?? []).map((r) => toLaunchProfile(r as Record<string, unknown>));
}

export async function seedBuiltinLaunchProfiles(projectId: string): Promise<LaunchProfile[]> {
  const existing = await getLaunchProfiles(projectId);
  if (existing.length > 0) return existing;

  const sb = getSupabaseClient();
  const inserts = BUILTIN_PROFILES.map((p) => ({
    project_id: projectId,
    name: p.name,
    description: p.description,
    payload: p.phases,
  }));
  const { data, error } = await sb.from('launch_profiles').insert(inserts).select();
  if (error) throw error;
  return (data ?? []).map((r) => toLaunchProfile(r as Record<string, unknown>));
}

/**
 * The explicit "use the pattern's workload" action (canon 111: a pattern enters
 * demand only through an explicit action, with its source recorded). Turns an
 * item's date-anchored workload lines into a workload template named after the
 * pattern versions they came from — "Pattern · Product launch v3" — reusing one
 * with that exact name, so every item with the same types shares one template.
 * Start-anchored lines are left to the rough cut; a launch plan hangs off the date.
 * Returns null when the item has no date-anchored workload.
 */
export async function ensurePatternWorkloadTemplate(
  projectId: string,
  lines: SourcedWorkloadLine[],
): Promise<LaunchProfile | null> {
  const dated = lines.filter((l) => l.anchor === 'date');
  if (dated.length === 0) return null;
  const sources = [...new Map(dated.map((l) => [l.source.patternId ?? l.source.typeValue, l.source])).values()];
  const name = `Pattern · ${sources
    .map((s) => `${s.typeLabel}${s.patternVersion ? ` v${s.patternVersion}` : ''}`)
    .join(' + ')}`;
  const existing = (await getLaunchProfiles(projectId)).find((p) => p.name === name);
  if (existing) return existing;
  return upsertLaunchProfile(projectId, {
    name,
    description: `Made from ${sources.map((s) => `${s.listName}: ${s.typeLabel}`).join('; ')}. Change the pattern in Settings → Type Lists & Patterns; a new version makes a new template.`,
    phases: dated.map((l, i) => ({
      id: `pattern-${i}`,
      label: `${l.source.typeLabel} · ${l.resourceType}`,
      resourceType: l.resourceType,
      offsetStartWeeks: l.fromWeek,
      // A line's weeks are inclusive; a phase ends at the start of the week after.
      offsetEndWeeks: l.toWeek + 1,
      hours: Math.round(l.capacityUnits * l.effortMultiplier * HOURS_PER_CU * 10) / 10,
    })),
  });
}

export async function upsertLaunchProfile(
  projectId: string,
  profile: Partial<LaunchProfile> & { name: string; phases: LaunchProfilePhase[] },
): Promise<LaunchProfile> {
  const sb = getSupabaseClient();
  const payload = {
    project_id: projectId,
    name: profile.name,
    description: profile.description ?? null,
    payload: profile.phases,
  };
  if (profile.id) {
    const { data, error } = await sb
      .from('launch_profiles')
      .update(payload)
      .eq('id', profile.id)
      .select()
      .single();
    if (error) throw error;
    return toLaunchProfile(data as Record<string, unknown>);
  }
  const { data, error } = await sb.from('launch_profiles').insert(payload).select().single();
  if (error) throw error;
  return toLaunchProfile(data as Record<string, unknown>);
}

export async function getSupplyCalendar(scenarioId: string): Promise<SupplyCalendarRow[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_supply_calendar')
    .select('*')
    .eq('scenario_id', scenarioId)
    .order('resource_type')
    .order('bucket_start');
  if (error) throw error;
  return (data ?? []).map((r) => toSupplyRow(r as Record<string, unknown>));
}

export async function upsertSupplyCalendarRows(rows: Omit<SupplyCalendarRow, 'id'>[]): Promise<void> {
  if (!rows.length) return;
  const sb = getSupabaseClient();
  const { error } = await sb.from('planning_supply_calendar').upsert(
    rows.map((r) => ({
      scenario_id: r.scenarioId,
      resource_type: r.resourceType,
      resource_name: r.resourceName ?? null,
      bucket_start: r.bucketStart,
      bucket_end: r.bucketEnd,
      supply_hours: r.supplyHours,
    })),
    { onConflict: 'scenario_id,resource_type,bucket_start' },
  );
  if (error) throw error;
}

function isLaunchGateTeam(team: string): boolean {
  const canonical = canonicalizeResourceType(team);
  const key = (canonical ?? team).toLowerCase().replace(/[\s-]+/g, '_');
  return LAUNCH_GATE_TEAMS.has(key);
}

/** Inclusive date-window overlap; missing either side → no mismatch. */
function windowsOverlap(
  aStart?: string | null,
  aEnd?: string | null,
  bStart?: string | null,
  bEnd?: string | null,
): boolean {
  if ((!aStart && !aEnd) || (!bStart && !bEnd)) return true;
  const as = aStart ?? aEnd!;
  const ae = aEnd ?? aStart!;
  const bs = bStart ?? bEnd!;
  const be = bEnd ?? bStart!;
  return as <= be && bs <= ae;
}

export async function buildTimePhasedInput(
  projectId: string,
  scenarioId: string,
): Promise<TimePhasedEngineInput> {
  const [scenarios, priorities, profiles, participation] = await Promise.all([
    getScenarios(projectId),
    getPriorities(projectId),
    getLaunchProfiles(projectId),
    getTeamParticipation(projectId),
  ]);

  const scenario = scenarios.find((s) => s.id === scenarioId);
  if (!scenario) throw new Error('Scenario not found');

  const horizon = scenario.horizonStartDate && scenario.horizonEndDate
    ? { start: scenario.horizonStartDate, end: scenario.horizonEndDate }
    : defaultHorizonFromScenario(scenario.planningWeeks);

  const profileById = new Map(profiles.map((p) => [p.id, p]));
  // Prefer scenario-owned rows so a Keith-only scenario stays clean.
  // Fall back to unbound (null scenario) only when the scenario has none yet.
  const scopedToScenario = priorities.filter((p) => p.scenarioId === scenarioId);
  const scenarioPriorities =
    scopedToScenario.length > 0
      ? scopedToScenario
      : priorities.filter((p) => !p.scenarioId || p.scenarioId === '');

  // Viability must not mix other scenarios' 300-item backlog into a Keith-only set.
  let viability: Awaited<ReturnType<typeof runEngine>>;
  if (scopedToScenario.length > 0) {
    const pidSet = new Set(scenarioPriorities.map((p) => p.id));
    const [capacityRows, priorityCapacityRows, workUnits, dependencies, priorityDependencies] =
      await Promise.all([
        getCapacityRows(projectId, scenarioId),
        getPriorityCapacityRows(projectId, scenarioId),
        getWorkUnits(projectId),
        getDependencies(projectId),
        getPriorityDependencies(projectId),
      ]);
    viability = runPlanningEngine({
      scenario,
      priorities: scenarioPriorities,
      workUnits: workUnits.filter((w) => w.priorityId == null || pidSet.has(w.priorityId)),
      dependencies,
      priorityDependencies: priorityDependencies.filter(
        (d) => pidSet.has(d.fromPriorityId) && pidSet.has(d.toPriorityId),
      ),
      capacityRows,
      priorityCapacityRows,
      teamParticipation: participation.filter(
        (t) => t.priorityId != null && pidSet.has(t.priorityId),
      ),
    });
  } else {
    viability = await runEngine(projectId, scenarioId);
  }

  const today = new Date().toISOString().slice(0, 10);

  // Taxonomy lives on items — join for predictability / fixed-vs-flexible defaults.
  const priorityIds = scenarioPriorities.map((p) => p.id);
  const taxonomyById = new Map<
    string,
    { work_type: string | null; launch_type: string | null; investment_type: string | null }
  >();
  if (priorityIds.length > 0) {
    const { data: itemRows } = await getSupabaseClient()
      .from('items')
      .select('id, work_type, launch_type, investment_type')
      .in('id', priorityIds);
    for (const row of itemRows ?? []) {
      taxonomyById.set(row.id as string, {
        work_type: (row.work_type as string | null) ?? null,
        launch_type: (row.launch_type as string | null) ?? null,
        investment_type: (row.investment_type as string | null) ?? null,
      });
    }
    // A plan in force keeps the types it authorized until it is amended (migration 1019).
    const held = await fetchHeldValues(priorityIds);
    for (const [itemId, values] of held ?? []) {
      taxonomyById.set(itemId, { work_type: values.workType, launch_type: values.launchType, investment_type: values.investmentType });
    }
  }

  // Type patterns (migration 918): an item's types' Certainty and Bar replace the
  // hard-coded type-name matching below. A type with no pattern keeps it.
  const patternContext = await loadPatternContext(projectId, priorityIds).catch(() => null);

  // Item Effort is stored in CUs (8h each). Prefer HOURS_PER_CAPACITY_UNIT over
  // scenario default so assumed launch demand matches Prioritization Item Effort.
  const hoursPerCu = HOURS_PER_CAPACITY_UNIT;
  const workspaceProfileOverrides = await getWorkspacePredictabilityProfiles(projectId).catch(
    () => ({}),
  );
  const workspacePredictabilityProfiles =
    toDerivePredictabilityWorkspaceProfiles(workspaceProfileOverrides);

  const priorityInputs: TimePhasedPriorityInput[] = scenarioPriorities.map((p) => {
    const pr = viability.priorityPlanRecords?.find((r) => r.priority.id === p.id);
    const included = pr
      ? pr.planDecision === 'Included' || pr.decisionWithParticipation === 'Included'
      : true;
    let atRisk = pr?.decisionWithParticipation?.includes('At Risk') ?? false;
    const cu =
      p.priorityLevelWuEstimate ??
      p.remainingWuEstimate ??
      (p.effortScore != null ? p.effortScore : null);
    const effortCapacityHours =
      cu != null && cu > 0 ? Number(cu) * hoursPerCu : undefined;

    const gateRows = participation.filter(
      (r) => r.priorityId === p.id && isLaunchGateTeam(r.requiredTeam),
    );
    let participationCommitted: boolean | undefined;
    let participationOverdue = false;
    let participationWindowMismatch = false;
    if (gateRows.length > 0) {
      participationCommitted = gateRows.every((r) => {
        if (participationUsesPeriodGrain(r)) {
          return periodsCoverLaunchWindow(
            r.periodCommitments,
            p.targetLaunchStart,
            p.targetLaunchEnd,
          ).covered;
        }
        if (r.requestStatus === 'confirmed') return true;
        return r.committed === 'yes';
      });
      participationOverdue = gateRows.some(
        (r) =>
          r.requestStatus !== 'confirmed' &&
          r.committed !== 'yes' &&
          !!r.participationDeadline &&
          r.participationDeadline < today,
      );
      participationWindowMismatch = gateRows.some((r) => {
        if (participationUsesPeriodGrain(r)) {
          const cover = periodsCoverLaunchWindow(
            r.periodCommitments,
            p.targetLaunchStart,
            p.targetLaunchEnd,
          );
          return cover.missingBuckets.length > 0 || cover.unconfirmedBuckets.length > 0;
        }
        return (
          r.committed === 'yes' &&
          !windowsOverlap(
            r.committedWindowStart,
            r.committedWindowEnd,
            p.targetLaunchStart,
            p.targetLaunchEnd,
          )
        );
      });
      if (!participationCommitted || participationOverdue || participationWindowMismatch) {
        atRisk = true;
      }
    }

    const tax = taxonomyById.get(p.id);
    const fromTypeNames = derivePredictability({
      workType: tax?.work_type,
      launchType: tax?.launch_type,
      investmentType: tax?.investment_type,
      workspaceProfiles: workspacePredictabilityProfiles,
    });
    const fromPatterns = patternContext
      ? predictabilityFromAssumptions(
          assumptionsFor(patternContext, { id: p.id, ...(tax ?? {}) }),
          fromTypeNames,
        )
      : null;
    const predictability = fromPatterns ?? fromTypeNames;
    // Explicit notes: anchoring:fixed|flexible (also accepts legacy flexibility:)
    const notesFlex = parseAnchoringFromNotes(p.notes);
    const launchFlexibility = resolveLaunchFlexibility(
      notesFlex,
      predictability.suggestedFlexibility,
    );

    return {
      id: p.id,
      name: p.name,
      forceRank: p.forceRank ?? 9999,
      owningOrg: p.owningOrg ?? undefined,
      owningOrgUnitId: p.owningOrgUnitId ?? undefined,
      targetLaunchStart: p.targetLaunchStart ?? undefined,
      targetLaunchEnd: p.targetLaunchEnd ?? undefined,
      launchProfileId: p.launchProfileId ?? undefined,
      launchProfile: p.launchProfileId ? profileById.get(p.launchProfileId) : undefined,
      effortCapacityHours,
      planDecision: pr?.planDecision,
      decisionWithParticipation: pr?.decisionWithParticipation,
      viabilityIncluded: included,
      participationAtRisk: atRisk,
      participationCommitted,
      participationOverdue,
      participationWindowMismatch,
      workType: tax?.work_type,
      launchType: tax?.launch_type,
      investmentType: tax?.investment_type,
      launchFlexibility,
      anchoring: launchFlexibility,
      slackWeeks: predictability.slackWeeks,
      calendarConfidence: predictability.calendarConfidence,
      commitmentFirmness: predictability.commitment_firmness,
      uncertaintyFactor: predictability.uncertainty_factor,
      confidenceCeiling: predictability.confidence_ceiling,
    };
  });

  const defaultHours = await getWorkspaceDefaultWeeklyHours(projectId);
  const supply = await ensureDefaultSupplyCalendar(
    scenarioId,
    horizon.start,
    horizon.end,
    defaultHours,
    projectId,
  );

  return {
    scenarioId,
    horizonStart: horizon.start,
    horizonEnd: horizon.end,
    priorities: priorityInputs,
    supplyCalendar: supply,
    defaultHoursPerWeek: defaultHours,
  };
}

export type TimePhasedRunResult = TimePhasedEngineOutput & {
  financial: TimePhasedFinancialOutput;
  assumptions: TimePhasedAssumptionsSnapshot;
};

async function persistTimePhasedAssumptions(
  scenarioId: string,
  snapshot: TimePhasedAssumptionsSnapshot,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_scenarios')
    .update({ time_phased_assumptions: snapshot })
    .eq('id', scenarioId);
  if (error) throw error;
}

export async function getLatestTimePhasedAssumptions(
  scenarioId: string,
): Promise<TimePhasedAssumptionsSnapshot | null> {
  const { data, error } = await getSupabaseClient()
    .from('planning_scenarios')
    .select('time_phased_assumptions')
    .eq('id', scenarioId)
    .single();
  if (error) throw error;
  const payload = data?.time_phased_assumptions as TimePhasedAssumptionsSnapshot | null | undefined;
  if (!payload?.assumptions?.length) return null;
  return payload;
}

export async function runTimePhasedPlacement(
  projectId: string,
  scenarioId: string,
): Promise<TimePhasedRunResult> {
  const input = await buildTimePhasedInput(projectId, scenarioId);
  const output = runTimePhasedPlacementEngine(input);
  const financial = await runFinancialForPlacements(
    projectId,
    scenarioId,
    input.horizonStart,
    input.horizonEnd,
    output.placements,
  );
  const previous = await getLatestTimePhasedAssumptions(scenarioId).catch(() => null);
  const fresh = deriveTimePhasedAssumptions(input, output, financial);
  const assumptions = mergePreservedAssumptionStatus(
    { ...fresh, runKey: `${scenarioId}:${fresh.ranAt}` },
    previous,
  );
  invalidateShadowImpactCache();
  await Promise.all([
    persistPlacements(scenarioId, output.placements, input.priorities),
    persistUtilizationSnapshot(scenarioId, output),
    persistTimePhasedAssumptions(scenarioId, assumptions),
  ]);
  // Cycle-bound scenarios: bridge rich run assumptions into plan_version.assumptions.
  const scenario = (await getScenarios(projectId)).find((s) => s.id === scenarioId);
  if (scenario?.planCycleId) {
    await syncAssumptionsToPlanVersion(scenario.planCycleId, assumptions).catch((err) => {
      console.warn('[runTimePhasedPlacement] plan-version assumption bridge failed', err);
    });
  }
  return { ...output, financial, assumptions };
}

function mapAssumptionStatusForPlanVersion(
  status: TimePhasedAssumptionStatus,
): AssumptionStatus {
  return status;
}

/**
 * Bridge anchor rule: assumption mirrors target the authorized POR's plan version
 * when a current POR exists; otherwise the latest (pre-authorization) version.
 * Latest version ≠ plan of record — an unauthorized commit must not steal the mirrors.
 */
export function pickBridgeTargetPlanVersionId(
  porPlanVersionId: string | null,
  latestPlanVersionId: string | null,
): string | null {
  return porPlanVersionId ?? latestPlanVersionId ?? null;
}

async function resolveBridgeTargetPlanVersionId(planCycleId: string): Promise<string | null> {
  const [{ data: por }, { data: latest }] = await Promise.all([
    getCurrentPorSnapshot(planCycleId),
    getLatestPlanVersion(planCycleId),
  ]);
  return pickBridgeTargetPlanVersionId(por?.plan_version_id ?? null, latest?.id ?? null);
}

/**
 * Replace engine-synced rows on the bridge target plan version (source_key = tp:*) —
 * the POR's version when one exists, else the latest. Human-entered assumptions
 * (source_key null) are left alone.
 */
export async function syncAssumptionsToPlanVersion(
  planCycleId: string,
  snapshot: TimePhasedAssumptionsSnapshot,
): Promise<{ planVersionId: string; synced: number } | null> {
  const planVersionId = await resolveBridgeTargetPlanVersionId(planCycleId);
  if (!planVersionId) return null;

  const sb = getSupabaseClient();
  // Prefer source_key filter; fall back to statement prefix if column missing pre-migration.
  const { error: delErr } = await sb
    .from('assumptions')
    .delete()
    .eq('plan_version_id', planVersionId)
    .like('source_key', 'tp:%');
  if (delErr) {
    // Pre-629 DBs: best-effort delete by statement marker
    await sb
      .from('assumptions')
      .delete()
      .eq('plan_version_id', planVersionId)
      .like('statement', '[tp:%');
  }

  if (snapshot.assumptions.length === 0) {
    return { planVersionId, synced: 0 };
  }

  const rows = snapshot.assumptions.map((a) => ({
    plan_version_id: planVersionId,
    statement: a.statement,
    status: mapAssumptionStatusForPlanVersion(a.status),
    source_key: `tp:${a.id}`,
  }));

  const { error: insErr } = await sb.from('assumptions').insert(rows);
  if (insErr) throw insErr;
  return { planVersionId, synced: rows.length };
}

/** Human sets confirmed | broken | unvalidated — engine never auto-confirms. */
export async function setAssumptionHumanStatus(
  scenarioId: string,
  assumptionId: string,
  status: TimePhasedAssumptionStatus,
): Promise<TimePhasedAssumptionsSnapshot | null> {
  const snap = await getLatestTimePhasedAssumptions(scenarioId);
  if (!snap) return null;
  const assumptions = snap.assumptions.map((a) =>
    a.id === assumptionId ? { ...a, status, humanSet: true } : a,
  );
  const next = { ...snap, assumptions };
  await persistTimePhasedAssumptions(scenarioId, next);

  // Mirror human status onto bridged plan-version row when present.
  const { data: scenarioRow } = await getSupabaseClient()
    .from('planning_scenarios')
    .select('plan_cycle_id')
    .eq('id', scenarioId)
    .maybeSingle();
  const planCycleId = scenarioRow?.plan_cycle_id as string | null | undefined;
  if (planCycleId) {
    const versionId = await resolveBridgeTargetPlanVersionId(planCycleId);
    if (versionId) {
      await getSupabaseClient()
        .from('assumptions')
        .update({ status: mapAssumptionStatusForPlanVersion(status) })
        .eq('plan_version_id', versionId)
        .eq('source_key', `tp:${assumptionId}`);
    }
  }
  return next;
}

/** Shift a flexible priority’s target launch by N weeks (calendar drag). */
export async function shiftPriorityLaunchWeeks(
  priorityId: string,
  deltaWeeks: number,
): Promise<void> {
  if (deltaWeeks === 0) return;
  const { data, error } = await getSupabaseClient()
    .from('planning_priorities')
    .select('id, notes, target_launch_start, target_launch_end')
    .eq('id', priorityId)
    .single();
  if (error) throw error;
  const anchoring = parseAnchoringFromNotes((data.notes as string | null) ?? '');
  if (anchoring === 'fixed') {
    throw new Error('Fixed (hard) launches cannot be dragged — unlock to flexible first');
  }
  const days = deltaWeeks * 7;
  const addDays = (iso: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };
  await updatePriority(priorityId, {
    targetLaunchStart: addDays(data.target_launch_start as string | null) ?? undefined,
    targetLaunchEnd: addDays(data.target_launch_end as string | null) ?? undefined,
  });
}

/** Persist fixed/flexible anchoring on a planning priority (notes tag). */
export async function setPriorityAnchoring(
  priorityId: string,
  anchoring: PlacementAnchoring,
  currentNotes?: string | null,
): Promise<void> {
  let notes = currentNotes;
  if (notes == null) {
    const { data } = await getSupabaseClient()
      .from('planning_priorities')
      .select('notes')
      .eq('id', priorityId)
      .single();
    notes = (data?.notes as string | null) ?? '';
  }
  await updatePriority(priorityId, {
    notes: upsertAnchoringInNotes(notes, anchoring),
  });
}

export { getLatestFinancialUtilization };

async function persistUtilizationSnapshot(
  scenarioId: string,
  output: TimePhasedEngineOutput,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_scenarios')
    .update({
      time_phased_utilization: {
        algorithmVersion: output.algorithmVersion,
        bucketUtilization: output.bucketUtilization,
        summary: output.summary,
        ranAt: new Date().toISOString(),
      },
    })
    .eq('id', scenarioId);
  if (error) throw error;
}

export async function getLatestUtilization(scenarioId: string): Promise<BucketUtilization[]> {
  const { data, error } = await getSupabaseClient()
    .from('planning_scenarios')
    .select('time_phased_utilization')
    .eq('id', scenarioId)
    .single();
  if (error) throw error;
  const payload = data?.time_phased_utilization as
    | { bucketUtilization?: BucketUtilization[] }
    | null
    | undefined;
  return payload?.bucketUtilization ?? [];
}

async function persistPlacements(
  scenarioId: string,
  placements: TimePhasedPlacement[],
  priorities: TimePhasedPriorityInput[],
): Promise<void> {
  const sb = getSupabaseClient();
  const nameById = new Map(priorities.map((p) => [p.id, p.name]));

  await sb.from('time_phased_placements').delete().eq('scenario_id', scenarioId);

  if (!placements.length) return;

  const orgById = new Map(priorities.map((p) => [p.id, p.owningOrg]));
  const rows = placements.map((p) => ({
    scenario_id: scenarioId,
    priority_id: p.priorityId,
    feasible_start: p.feasibleStart ?? null,
    feasible_end: p.feasibleEnd ?? null,
    target_launch_start: p.targetLaunchStart ?? null,
    target_launch_end: p.targetLaunchEnd ?? null,
    slip_weeks: p.slipWeeks ?? null,
    placement_status: p.placementStatus,
    buildable: p.buildable,
    launchable: p.launchable,
    placement_rationale: {
      ...p.rationale,
      priorityName: nameById.get(p.priorityId),
      owningOrg: orgById.get(p.priorityId) ?? null,
    },
  }));

  const { error } = await sb.from('time_phased_placements').insert(rows);
  if (error) throw error;
}

export async function getLatestPlacements(scenarioId: string): Promise<TimePhasedPlacement[]> {
  const { data, error } = await getSupabaseClient()
    .from('time_phased_placements')
    .select('*')
    .eq('scenario_id', scenarioId);
  if (error) throw error;
  return (data ?? []).map((row) => {
    const p = toPlacement(row as Record<string, unknown>);
    if (!p.priorityName) p.priorityName = p.priorityId;
    return p;
  });
}

/**
 * One-shot guided setup: horizon + built-in profiles + default supply +
 * staggered launch windows on priorities + first placement run.
 * Idempotent — safe to call on every "Set up" click.
 */
export async function setupTimePhasedPlanning(
  projectId: string,
  scenarioId: string,
  planningWeeks: number,
): Promise<TimePhasedRunResult> {
  const horizon = await ensureScenarioHorizon(scenarioId, planningWeeks);
  const [profiles] = await Promise.all([
    seedBuiltinLaunchProfiles(projectId),
    ensureDefaultSupplyCalendar(
      scenarioId,
      horizon.start,
      horizon.end,
      undefined,
      projectId,
    ),
  ]);
  await seedPriorityLaunchWindows(projectId, scenarioId, profiles, horizon.start);
  return runTimePhasedPlacement(projectId, scenarioId);
}

/**
 * Assign the default workload template to unassigned priorities and stagger
 * target launch dates so the first calendar is immediately readable.
 * Does not overwrite existing profile / window assignments.
 */
export async function seedPriorityLaunchWindows(
  projectId: string,
  scenarioId: string,
  profiles: LaunchProfile[],
  horizonStart: string,
): Promise<number> {
  const defaultProfileId = profiles[0]?.id;
  if (!defaultProfileId) return 0;

  const priorities = (await getPriorities(projectId)).filter(
    (p) => p.scenarioId === scenarioId || !p.scenarioId,
  );
  priorities.sort((a, b) => (a.forceRank ?? 9999) - (b.forceRank ?? 9999));

  let updated = 0;
  for (let i = 0; i < priorities.length; i++) {
    const p = priorities[i];
    const needsProfile = !p.launchProfileId;
    const needsWindow = !p.targetLaunchStart && !p.targetLaunchEnd;
    if (!needsProfile && !needsWindow) continue;

    // Stagger by 2 weeks from horizon + 8 weeks build buffer, by force rank.
    const offsetDays = 56 + i * 14;
    const start = needsWindow ? addDaysIso(horizonStart, offsetDays) : undefined;
    const end = start ? addDaysIso(start, 14) : undefined;

    await updatePriorityLaunchSetup(p.id, {
      ...(needsProfile ? { launchProfileId: defaultProfileId } : {}),
      ...(needsWindow && start && end
        ? { targetLaunchStart: start, targetLaunchEnd: end }
        : {}),
    });
    updated += 1;
  }
  return updated;
}

function addDaysIso(isoDate: string, days: number): string {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export async function runCounterfactual(
  projectId: string,
  scenarioId: string,
  lever: 'promote' | 'delay',
  priorityId: string,
  delayWeeks = 4,
  userId?: string,
): Promise<{
  base: TimePhasedEngineOutput;
  hypothesis: TimePhasedEngineOutput;
  diff: ScenarioDiffEntry[];
  changeSet: CalendarChangeSet;
  hypothesisScenario: Scenario;
}> {
  const baseInput = await buildTimePhasedInput(projectId, scenarioId);
  const base = runTimePhasedPlacementEngine(baseInput);

  let hypothesisPriorities = baseInput.priorities;
  if (lever === 'promote') {
    hypothesisPriorities = applyPromoteLever(hypothesisPriorities, priorityId);
  } else {
    hypothesisPriorities = applyDelayLever(hypothesisPriorities, priorityId, delayWeeks);
  }

  const hypothesis = runTimePhasedPlacementEngine({
    ...baseInput,
    priorities: hypothesisPriorities,
  });

  const targetName =
    baseInput.priorities.find((p) => p.id === priorityId)?.name ?? 'priority';
  const leverLabel = lever === 'promote' ? 'Promote' : `Delay ${delayWeeks}wk`;

  // Durable hypothesis scenario: copy capacity + horizon/supply, persist placements.
  // Re-running the same lever on the same target REUSES the existing hypothesis
  // scenario instead of minting an identical clone in the picker.
  const allScenarios = await getScenarios(projectId);
  const baseName =
    allScenarios.find((s) => s.id === scenarioId)?.name.replace(/ \(copy\)$/, '') ?? 'Scenario';
  const hypothesisName = `${baseName} — ${leverLabel}: ${targetName}`.slice(0, 120);
  const existingHypothesis = allScenarios.find(
    (s) => s.id !== scenarioId && s.name === hypothesisName,
  );

  let hypothesisScenario: Scenario;
  if (existingHypothesis) {
    hypothesisScenario = await updateScenario(existingHypothesis.id, {
      horizonStartDate: baseInput.horizonStart,
      horizonEndDate: baseInput.horizonEnd,
      timePhasedEnabled: true,
      notes: `Counterfactual (${lever}) on ${targetName}. Generated from scenario ${scenarioId}.`,
    });
  } else {
    const draft = await duplicateScenario(scenarioId);
    hypothesisScenario = await updateScenario(draft.id, {
      name: hypothesisName,
      horizonStartDate: baseInput.horizonStart,
      horizonEndDate: baseInput.horizonEnd,
      timePhasedEnabled: true,
      notes: `Counterfactual (${lever}) on ${targetName}. Generated from scenario ${scenarioId}.`,
    });
  }

  const supply = await getSupplyCalendar(scenarioId);
  if (supply.length > 0) {
    await upsertSupplyCalendarRows(
      supply.map((r) => ({
        scenarioId: hypothesisScenario.id,
        resourceType: r.resourceType,
        resourceName: r.resourceName,
        bucketStart: r.bucketStart,
        bucketEnd: r.bucketEnd,
        supplyHours: r.supplyHours,
      })),
    );
  }

  // Envelope copy is insert-shaped — only on first creation, or reuse duplicates rows.
  if (!existingHypothesis) {
    await copyFundingEnvelopesToScenario(scenarioId, hypothesisScenario.id, projectId);
  }

  const [costPlans, envelopesBase] = await Promise.all([
    getCostPlansForScenario(projectId, scenarioId),
    getFundingEnvelopes(scenarioId),
  ]);
  // Hypotheses share cost plans (priority-scoped); envelopes were copied above.
  const envelopesHyp = await getFundingEnvelopes(hypothesisScenario.id);
  const baseFin = runTimePhasedFinancialEngine({
    horizonStart: baseInput.horizonStart,
    horizonEnd: baseInput.horizonEnd,
    placements: base.placements,
    costPlans,
    envelopes: envelopesBase,
  });
  const hypFin = runTimePhasedFinancialEngine({
    horizonStart: baseInput.horizonStart,
    horizonEnd: baseInput.horizonEnd,
    placements: hypothesis.placements,
    costPlans,
    envelopes: envelopesHyp,
  });

  await Promise.all([
    persistPlacements(scenarioId, base.placements, baseInput.priorities),
    persistPlacements(hypothesisScenario.id, hypothesis.placements, hypothesisPriorities),
    persistUtilizationSnapshot(scenarioId, base),
    persistUtilizationSnapshot(hypothesisScenario.id, hypothesis),
    persistFinancialSnapshot(scenarioId, baseFin),
    persistFinancialSnapshot(hypothesisScenario.id, hypFin),
  ]);

  const changeSet = buildCalendarChangeSet({
    basePlacements: base.placements,
    hypothesisPlacements: hypothesis.placements,
    baseUtilization: base.bucketUtilization,
    hypothesisUtilization: hypothesis.bucketUtilization,
    lever,
    leverPriorityId: priorityId,
    leverPriorityName: targetName,
    delayWeeks,
    baseScenarioId: scenarioId,
    hypothesisScenarioId: hypothesisScenario.id,
    baseMoneyUtilization: baseFin.moneyUtilization,
    hypothesisMoneyUtilization: hypFin.moneyUtilization,
    hypothesisAffordability: hypFin.affordability,
  });

  await markParticipationNeedsReconfirm(projectId, changeSet);

  const sb = getSupabaseClient();
  const { data: diffRow } = await sb
    .from('scenario_diffs')
    .insert({
      project_id: projectId,
      base_scenario_id: scenarioId,
      hypothesis_scenario_id: hypothesisScenario.id,
      lever_type: lever,
      lever_payload: { priorityId, delayWeeks, priorityName: targetName },
      diff_payload: changeSet,
      created_by: userId ?? null,
    })
    .select('id')
    .single();

  if (diffRow?.id) changeSet.id = diffRow.id as string;

  return {
    base,
    hypothesis,
    diff: changeSet.entries,
    changeSet,
    hypothesisScenario,
  };
}

/** Soft-stale committed participation when the calendar delta requires reconfirm. */
async function markParticipationNeedsReconfirm(
  projectId: string,
  changeSet: CalendarChangeSet,
): Promise<void> {
  if (changeSet.reconfirm.length === 0) return;
  const rows = await getTeamParticipation(projectId);
  const touched = new Set<string>();
  for (const req of changeSet.reconfirm) {
    const teamKey = canonicalizeResourceType(req.team);
    const match = rows.find(
      (r) =>
        r.priorityId === req.priorityId &&
        canonicalizeResourceType(r.requiredTeam) === teamKey &&
        !touched.has(r.id),
    );
    if (!match) continue;
    const wasConfirmed =
      match.committed === 'yes' ||
      match.requestStatus === 'confirmed' ||
      match.periodCommitments.some((p) => p.status === 'confirmed');
    if (!wasConfirmed) continue;
    touched.add(match.id);
    const note = `[Needs reconfirm] ${req.reason}`;
    const periods = staleConfirmedPeriods(match.periodCommitments);
    await updateTeamParticipation(match.id, {
      committed: 'partial',
      requestStatus: rollupRequestStatus(periods, 'tentative'),
      periodCommitments: periods,
      notes: match.notes?.includes('[Needs reconfirm]')
        ? match.notes
        : `${note}${match.notes ? ` · ${match.notes}` : ''}`,
    });
  }
}

export function buildScenarioDiff(
  base: TimePhasedPlacement[],
  hypothesis: TimePhasedPlacement[],
): ScenarioDiffEntry[] {
  return buildCalendarChangeSet({
    basePlacements: base,
    hypothesisPlacements: hypothesis,
    baseUtilization: [],
    hypothesisUtilization: [],
    lever: 'promote',
    leverPriorityId: '',
    leverPriorityName: '',
    baseScenarioId: '',
    hypothesisScenarioId: '',
  }).entries;
}

/** Latest calendar change set for a base scenario (most recent counterfactual). */
export async function getLatestCalendarChangeSet(
  baseScenarioId: string,
): Promise<CalendarChangeSet | null> {
  const { data, error } = await getSupabaseClient()
    .from('scenario_diffs')
    .select('id, lever_type, lever_payload, diff_payload, hypothesis_scenario_id, created_at')
    .eq('base_scenario_id', baseScenarioId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const payload = data.diff_payload;
  const leverPayload = (data.lever_payload ?? {}) as {
    priorityId?: string;
    priorityName?: string;
    delayWeeks?: number;
  };
  const changeSet = coerceChangeSet(payload, {
    lever: data.lever_type === 'delay' ? 'delay' : 'promote',
    leverPriorityId: leverPayload.priorityId,
    leverPriorityName: leverPayload.priorityName,
    delayWeeks: leverPayload.delayWeeks,
    baseScenarioId,
    hypothesisScenarioId: (data.hypothesis_scenario_id as string) ?? '',
    createdAt: data.created_at as string,
  });
  if (changeSet) changeSet.id = data.id as string;
  return changeSet;
}

/** Partial launch-setup update for one priority (profile / window / org). */
export async function updatePriorityLaunchSetup(
  priorityId: string,
  fields: {
    launchProfileId?: string | null;
    targetLaunchStart?: string | null;
    targetLaunchEnd?: string | null;
    owningOrg?: string | null;
    owningOrgUnitId?: string | null;
  },
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if ('launchProfileId' in fields) patch.launch_profile_id = fields.launchProfileId;
  if ('targetLaunchStart' in fields) patch.target_launch_start = fields.targetLaunchStart;
  if ('targetLaunchEnd' in fields) patch.target_launch_end = fields.targetLaunchEnd;
  if ('owningOrg' in fields) patch.owning_org = fields.owningOrg;
  if ('owningOrgUnitId' in fields) patch.owning_org_unit_id = fields.owningOrgUnitId;
  if (Object.keys(patch).length === 0) return;
  const { error } = await getSupabaseClient()
    .from('planning_priorities')
    .update(patch)
    .eq('id', priorityId);
  if (error) throw error;
}

export async function deleteLaunchProfile(profileId: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('launch_profiles')
    .delete()
    .eq('id', profileId);
  if (error) throw error;
}

export async function attachLaunchProfileToPriority(
  priorityId: string,
  launchProfileId: string,
  owningOrg?: string,
  targetLaunchStart?: string,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_priorities')
    .update({
      launch_profile_id: launchProfileId,
      owning_org: owningOrg ?? null,
      target_launch_start: targetLaunchStart ?? null,
      target_launch_end: targetLaunchStart ? addMonths(targetLaunchStart, 3) : null,
    })
    .eq('id', priorityId);
  if (error) throw error;
}

export async function savePortfolioRollupSources(
  scenarioId: string,
  sources: Omit<PortfolioRollupSource, 'id'>[],
): Promise<void> {
  const sb = getSupabaseClient();
  await sb.from('portfolio_rollup_sources').delete().eq('scenario_id', scenarioId);
  if (!sources.length) return;
  const { error } = await sb.from('portfolio_rollup_sources').insert(
    sources.map((s) => ({
      scenario_id: scenarioId,
      source_type: s.sourceType,
      source_reference_id: s.sourceReferenceId,
      source_label: s.sourceLabel ?? null,
      owning_org: s.owningOrg ?? null,
      owning_org_unit_id: s.owningOrgUnitId ?? null,
      sort_order: s.sortOrder,
    })),
  );
  if (error) throw error;
}

export async function getPortfolioRollupSources(scenarioId: string): Promise<PortfolioRollupSource[]> {
  const { data, error } = await getSupabaseClient()
    .from('portfolio_rollup_sources')
    .select('*')
    .eq('scenario_id', scenarioId)
    .order('sort_order');
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    scenarioId: r.scenario_id as string,
    sourceType: r.source_type as string,
    sourceReferenceId: r.source_reference_id as string,
    sourceLabel: r.source_label as string | undefined,
    owningOrg: r.owning_org as string | undefined,
    owningOrgUnitId: (r.owning_org_unit_id as string | undefined) ?? undefined,
    sortOrder: r.sort_order as number,
  }));
}

/** Import supply calendar from CSV rows: resource_type, bucket_start, supply_hours */
export async function importSupplyCalendarCsv(
  scenarioId: string,
  rows: {
    resourceType: string;
    bucketStart: string;
    supplyHours: number;
    resourceName?: string;
    bucketEnd?: string;
  }[],
): Promise<void> {
  const supplyRows: Omit<SupplyCalendarRow, 'id'>[] = rows.map((r) => ({
    scenarioId,
    resourceType: r.resourceType,
    resourceName: r.resourceName,
    bucketStart: r.bucketStart,
    bucketEnd: r.bucketEnd && r.bucketEnd !== r.bucketStart ? r.bucketEnd : addDaysIso(r.bucketStart, 6),
    supplyHours: r.supplyHours,
  }));
  await upsertSupplyCalendarRows(supplyRows);
}

export function exportSupplyCalendarCsv(rows: SupplyCalendarRow[]): string {
  const header = 'resource_type,resource_name,bucket_start,bucket_end,supply_hours';
  const lines = rows.map(
    (r) =>
      `${r.resourceType},${r.resourceName ?? ''},${r.bucketStart},${r.bucketEnd},${r.supplyHours}`,
  );
  return [header, ...lines].join('\n');
}

export function exportPlacementsCsv(placements: TimePhasedPlacement[]): string {
  const header =
    'priority_name,owning_org,feasible_start,feasible_end,status,buildable,launchable,slip_weeks';
  const lines = placements.map(
    (p) =>
      `"${p.priorityName}",${p.owningOrg ?? ''},${p.feasibleStart ?? ''},${p.feasibleEnd ?? ''},${p.placementStatus},${p.buildable},${p.launchable},${p.slipWeeks ?? ''}`,
  );
  return [header, ...lines].join('\n');
}

export async function ensureScenarioHorizon(
  scenarioId: string,
  planningWeeks: number,
): Promise<{ start: string; end: string }> {
  const sb = getSupabaseClient();
  const { data } = await sb
    .from('planning_scenarios')
    .select('horizon_start_date, horizon_end_date')
    .eq('id', scenarioId)
    .single();

  if (data?.horizon_start_date && data?.horizon_end_date) {
    return { start: data.horizon_start_date as string, end: data.horizon_end_date as string };
  }

  const horizon = defaultHorizonFromScenario(planningWeeks);
  await updateScenario(scenarioId, {
    horizonStartDate: horizon.start,
    horizonEndDate: horizon.end,
    timePhasedEnabled: true,
  });
  return horizon;
}

/** Downloadable CSV template for the supply calendar import. */
export function supplyCalendarCsvTemplate(horizonStart?: string): string {
  const start = horizonStart ?? new Date().toISOString().slice(0, 10);
  const end = addDaysIso(start, 6);
  const header = 'resource_type,resource_name,bucket_start,bucket_end,supply_hours';
  const examples = TEAMS.slice(0, 3).map(
    (t) => `${t.type},${t.label},${start},${end},40`,
  );
  return [header, ...examples].join('\n');
}

export function exportLaunchSetupCsv(
  rows: {
    name: string;
    forceRank?: number | null;
    launchProfileName?: string;
    targetLaunchStart?: string | null;
    targetLaunchEnd?: string | null;
    owningOrg?: string | null;
  }[],
): string {
  const header =
    'priority_name,force_rank,launch_profile,target_launch_start,target_launch_end,owning_org';
  const lines = rows.map(
    (r) =>
      `"${r.name.replace(/"/g, '""')}",${r.forceRank ?? ''},"${(r.launchProfileName ?? '').replace(/"/g, '""')}",${r.targetLaunchStart ?? ''},${r.targetLaunchEnd ?? ''},"${(r.owningOrg ?? '').replace(/"/g, '""')}"`,
  );
  return [header, ...lines].join('\n');
}

export function launchSetupCsvTemplate(): string {
  return [
    'priority_name,force_rank,launch_profile,target_launch_start,target_launch_end,owning_org',
    '"Example Priority",1,"Standard product launch",2026-04-01,2026-04-15,Platform',
  ].join('\n');
}

/**
 * Import launch setup by priority name (case-insensitive).
 * Matches profile by name; leaves unknown profile names untouched.
 */
export async function importLaunchSetupCsv(
  projectId: string,
  scenarioId: string,
  csvText: string,
): Promise<{ updated: number; unmatched: string[] }> {
  const lines = csvText.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { updated: 0, unmatched: [] };

  const header = lines[0].toLowerCase();
  const hasHeader = header.includes('priority_name') || header.includes('launch_profile');
  const dataLines = hasHeader ? lines.slice(1) : lines;

  const [priorities, profiles] = await Promise.all([
    getPriorities(projectId),
    getLaunchProfiles(projectId),
  ]);
  const scoped = priorities.filter((p) => p.scenarioId === scenarioId || !p.scenarioId);
  const byName = new Map(scoped.map((p) => [p.name.trim().toLowerCase(), p]));
  const profileByName = new Map(profiles.map((p) => [p.name.trim().toLowerCase(), p.id]));

  let updated = 0;
  const unmatched: string[] = [];

  for (const line of dataLines) {
    const parts = splitCsvLine(line);
    if (parts.length < 1) continue;
    const name = parts[0]?.trim();
    if (!name) continue;
    const priority = byName.get(name.toLowerCase());
    if (!priority) {
      unmatched.push(name);
      continue;
    }
    const profileName = parts[2]?.trim();
    const profileId = profileName ? profileByName.get(profileName.toLowerCase()) : undefined;
    await updatePriorityLaunchSetup(priority.id, {
      ...(profileId ? { launchProfileId: profileId } : {}),
      ...(parts[3] ? { targetLaunchStart: parts[3] } : {}),
      ...(parts[4] ? { targetLaunchEnd: parts[4] } : {}),
      ...(parts[5] !== undefined ? { owningOrg: parts[5] || null } : {}),
    });
    updated += 1;
  }

  return { updated, unmatched };
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const KEITH_DEMO_PREFIX = '[Keith demo]';

/**
 * Proof-of-life portfolio: three ranked launches, tight marketing supply, and
 * one unconfirmed participation request — enough to show buildable ≠ launchable
 * and overloaded PMM in under five minutes.
 */
export async function seedKeithDemoPortfolio(
  projectId: string,
  scenarioId: string,
  planningWeeks: number,
): Promise<{
  output: TimePhasedRunResult;
  createdPriorityIds: string[];
  message: string;
}> {
  const horizon = await ensureScenarioHorizon(scenarioId, planningWeeks);
  const profiles = await seedBuiltinLaunchProfiles(projectId);
  const defaultProfileId = profiles[0]?.id;
  if (!defaultProfileId) throw new Error('Could not seed workload templates');

  const existing = (await getPriorities(projectId)).filter(
    (p) =>
      (p.scenarioId === scenarioId || !p.scenarioId) &&
      p.name.startsWith(KEITH_DEMO_PREFIX),
  );

  const demoSpecs = [
    {
      name: `${KEITH_DEMO_PREFIX} Partner expansion — EMEA`,
      forceRank: 1,
      effortCu: 12,
      launchOffsetDays: 70,
      windowDays: 14,
      needParticipation: true,
    },
    {
      name: `${KEITH_DEMO_PREFIX} Pricing & packaging refresh`,
      forceRank: 2,
      effortCu: 8,
      launchOffsetDays: 84,
      windowDays: 14,
      needParticipation: true,
    },
    {
      name: `${KEITH_DEMO_PREFIX} Self-serve onboarding`,
      forceRank: 3,
      effortCu: 16,
      launchOffsetDays: 98,
      windowDays: 21,
      needParticipation: false,
    },
  ];

  const createdPriorityIds: string[] = [];
  const demoPriorityIds: { priorityId: string; forecastAmount: number; name?: string }[] = [];
  const byName = new Map(existing.map((p) => [p.name, p]));

  for (const spec of demoSpecs) {
    let priority = byName.get(spec.name);
    const start = addDaysIso(horizon.start, spec.launchOffsetDays);
    const end = addDaysIso(start, spec.windowDays);
    if (!priority) {
      priority = await createPriority(projectId, {
        scenarioId,
        name: spec.name,
        forceRank: spec.forceRank,
        priorityLevelWuEstimate: spec.effortCu,
        launchProfileId: defaultProfileId,
        targetLaunchStart: start,
        targetLaunchEnd: end,
        owningOrg: 'Product Marketing',
        aboveTheLine: true,
      });
      createdPriorityIds.push(priority.id);
    } else {
      await updatePriority(priority.id, {
        forceRank: spec.forceRank,
        priorityLevelWuEstimate: spec.effortCu,
        launchProfileId: defaultProfileId,
        targetLaunchStart: start,
        targetLaunchEnd: end,
        owningOrg: 'Product Marketing',
      });
    }
    demoPriorityIds.push({
      priorityId: priority.id,
      forecastAmount: spec.effortCu * 8000,
      name: `${spec.name} cost`,
    });

    if (spec.needParticipation) {
      const rows = await getTeamParticipation(projectId, { priorityId: priority.id });
      const hasMarketing = rows.some((r) => isLaunchGateTeam(r.requiredTeam));
      if (!hasMarketing) {
        const deadline = addDaysIso(new Date().toISOString().slice(0, 10), 7);
        const periods = fillPeriodCommitmentsFromWindow(start, end, 'requested', 8);
        await createTeamParticipation(projectId, {
          priorityId: priority.id,
          requiredTeam: 'marketing',
          requiredCapability: 'Launch campaign ownership',
          capacityRequiredWu: 4,
          capacityConfirmed: 'no',
          capabilityPresent: 'yes',
          committed: 'no',
          owner: 'PMM lead',
          participationDeadline: deadline,
          committedWindowStart: start,
          committedWindowEnd: end,
          requestStatus: 'requested',
          periodCommitments: periods,
          riskIfUnavailable: 'Cannot announce or enable partner motion on target date',
          notes: 'Keith demo — leave uncommitted to show buildable ≠ launchable',
        });
      }
    }
  }

  // Tight marketing supply (~8h/week) so the calendar shows overload.
  await ensureDefaultSupplyCalendar(scenarioId, horizon.start, horizon.end, 40, projectId);
  const supply = await getSupplyCalendar(scenarioId);
  const marketingRows = supply
    .filter((r) => canonicalizeResourceType(r.resourceType) === 'marketing')
    .map((r) => ({
      scenarioId,
      resourceType: r.resourceType,
      resourceName: r.resourceName,
      bucketStart: r.bucketStart,
      bucketEnd: r.bucketEnd,
      supplyHours: 8,
    }));
  if (marketingRows.length > 0) {
    await upsertSupplyCalendarRows(marketingRows);
  }

  // Keep viability capacity aligned for marketing if a row exists / can be created.
  const capacity = await getCapacityRows(projectId, scenarioId);
  const marketingCap = capacity.find(
    (c) => canonicalizeResourceType(c.resourceType) === 'marketing',
  );
  if (marketingCap) {
    // Capacity rows are updated via recreate — leave as-is if update API is heavy;
    // supply calendar is the time-phased source of truth.
  } else {
    try {
      await createCapacityRow(projectId, {
        scenarioId,
        resourceType: 'marketing',
        resourceName: 'Product Marketing',
        weeklyCapacityHours: 8,
        notes: 'Keith demo — constrained PMM',
      });
    } catch {
      // Optional — supply calendar still drives placement.
    }
  }

  // Tight weekly funding so money heat shows alongside hours (FIN-001 / FIN-AT-03).
  await seedDemoFinancialLayer({
    projectId,
    scenarioId,
    horizonStart: horizon.start,
    horizonEnd: horizon.end,
    prioritySpecs: demoPriorityIds,
    weeklyApproved: 12000,
    envelopeName: 'Keith demo funding',
  });

  const output = await runTimePhasedPlacement(projectId, scenarioId);
  await persistLiveScopeViabilityInstance(projectId, scenarioId);
  const bnL = output.placements.filter((p) => p.buildable && !p.launchable).length;
  const moneyNote = output.financial?.affordability?.withinEnvelope
    ? 'funding within envelope'
    : `funding overage $${Math.round(output.financial?.affordability?.overageTotal ?? 0).toLocaleString()}`;
  const message =
    `Keith demo ready: ${output.summary.total} launches placed ` +
    `(${output.summary.slipped} slipped, ${bnL} buildable but not launchable). ` +
    `Marketing supply is 8h/week; two priorities await participation commit. ` +
    `Money: ${moneyNote}.`;

  return { output, createdPriorityIds, message };
}

/** Dedicated Keith scenario — WP Engine–shaped portfolio, filterable via Item Pool. */
export const KEITH_COMMIT_SCENARIO_NAME = 'Keith · Shared-team commit';
export const KEITH_COMMIT_POOL_NAME = 'Keith · WP Engine shared-team portfolio';
export const KEITH_COMMIT_PREFIX = '[Keith]';

type KeithCommitTeam = 'marketing' | 'legal' | 'finance' | 'support' | 'design';

type KeithCommitSpec = {
  name: string;
  forceRank: number;
  effortCu: number;
  launchOffsetDays: number;
  windowDays: number;
  owningOrg: string;
  /** Shared teams that must confirm before the launch is launchable. */
  gateTeams: KeithCommitTeam[];
  forecastAmount: number;
  /** Marketing launch type slug (workspace_launch_types.value). */
  launchType: string;
  /** Financial investment type slug (workspace_investment_types.value). */
  investmentType: string;
  /** Optional work type for predictability (e.g. spike = experimental). */
  workType?: string;
};

/**
 * Representative 2025–26 WP Engine portfolio themes (AI Toolkit / Smart Search /
 * MCP / packaging / agency / support / UX) — shaped for Keith’s shared-team story,
 * not a claim about their live roadmap.
 */
/** Hard-blocking predecessor for AI Toolkit packaging (initiative → initiative). */
export const KEITH_BILLING_DELIVERY_NAME = `${KEITH_COMMIT_PREFIX} Billing System — team delivery`;
export const KEITH_PACKAGING_NAME = `${KEITH_COMMIT_PREFIX} AI Toolkit pricing & packaging`;

const KEITH_COMMIT_SPECS: KeithCommitSpec[] = [
  {
    // Ranked early but oversized so it lands Out - capacity — packaging
    // hard-depends on this and therefore shows Blocked in Above/Below.
    name: KEITH_BILLING_DELIVERY_NAME,
    forceRank: 1,
    effortCu: 200,
    launchOffsetDays: 28,
    windowDays: 42,
    owningOrg: 'Billing Platform',
    gateTeams: ['finance', 'legal'],
    forecastAmount: 120_000,
    launchType: 'platform_capability',
    investmentType: 'platform_infra',
  },
  {
    name: `${KEITH_COMMIT_PREFIX} Smart Search AI MCP — GA`,
    forceRank: 2,
    effortCu: 20,
    launchOffsetDays: 49,
    windowDays: 21,
    owningOrg: 'Platform',
    gateTeams: ['marketing', 'legal', 'support'],
    forecastAmount: 96_000,
    launchType: 'product_launch',
    investmentType: 'growth',
  },
  {
    name: `${KEITH_COMMIT_PREFIX} AI Recommendations — commerce pack`,
    forceRank: 3,
    effortCu: 12,
    launchOffsetDays: 63,
    windowDays: 14,
    owningOrg: 'Product',
    gateTeams: ['marketing', 'design'],
    forecastAmount: 72_000,
    launchType: 'feature_launch',
    investmentType: 'growth',
  },
  {
    name: `${KEITH_COMMIT_PREFIX} Managed Vector DB — self-serve`,
    forceRank: 4,
    effortCu: 24,
    launchOffsetDays: 70,
    windowDays: 21,
    owningOrg: 'Platform',
    gateTeams: ['legal', 'support'],
    forecastAmount: 88_000,
    launchType: 'platform_capability',
    investmentType: 'platform_infra',
  },
  {
    name: `${KEITH_COMMIT_PREFIX} Agency co-sell motion — EMEA`,
    forceRank: 5,
    effortCu: 10,
    launchOffsetDays: 56,
    windowDays: 14,
    owningOrg: 'Product Marketing',
    gateTeams: ['marketing', 'legal', 'finance'],
    forecastAmount: 64_000,
    launchType: 'partner_gtm',
    investmentType: 'growth',
  },
  {
    name: KEITH_PACKAGING_NAME,
    forceRank: 6,
    effortCu: 8,
    launchOffsetDays: 77,
    windowDays: 14,
    owningOrg: 'Finance',
    gateTeams: ['finance', 'marketing', 'legal'],
    forecastAmount: 48_000,
    launchType: 'pricing_packaging',
    investmentType: 'growth',
  },
  {
    name: `${KEITH_COMMIT_PREFIX} Support AI assist for hosting`,
    forceRank: 7,
    effortCu: 14,
    launchOffsetDays: 84,
    windowDays: 21,
    owningOrg: 'Customer Support',
    gateTeams: ['support', 'design'],
    forecastAmount: 56_000,
    launchType: 'support_enablement',
    investmentType: 'maintain',
  },
  {
    name: `${KEITH_COMMIT_PREFIX} AI Toolkit onboarding UX`,
    forceRank: 8,
    effortCu: 16,
    launchOffsetDays: 91,
    windowDays: 21,
    owningOrg: 'Experience Design',
    gateTeams: ['design', 'marketing'],
    forecastAmount: 60_000,
    launchType: 'feature_launch',
    investmentType: 'growth',
  },
  {
    // Slice 4 demo beat: experimental work shows more slack / lower confidence
    // than solid GA/product launches beside it on the Launch Calendar.
    name: `${KEITH_COMMIT_PREFIX} AI Toolkit discovery spike`,
    forceRank: 9,
    effortCu: 6,
    launchOffsetDays: 42,
    windowDays: 14,
    owningOrg: 'Product',
    gateTeams: ['design', 'marketing'],
    forecastAmount: 24_000,
    launchType: 'feature_launch',
    investmentType: 'venture_option',
    workType: 'spike',
  },
];

async function ensureKeithHardDependency(
  projectId: string,
  packagingId: string,
  billingId: string,
): Promise<void> {
  const existing = await getPriorityDependencies(projectId);
  const already = existing.some(
    (d) =>
      d.fromPriorityId === packagingId &&
      d.toPriorityId === billingId &&
      d.dependencyType === 'Hard' &&
      d.blocking,
  );
  if (already) return;
  await createPriorityDependency(projectId, {
    fromPriorityId: packagingId,
    toPriorityId: billingId,
    dependencyType: 'Hard',
    blocking: true,
    notes:
      'Keith commit — AI Toolkit packaging hard-blocked until Billing System team delivery is Included',
  });

  // Mirror into Prioritization item graph so Sync on Dependencies tab stays honest.
  const sb = getSupabaseClient();
  const { data: edgeRows } = await sb
    .from('item_relationship_edges')
    .select('id')
    .eq('source_item_id', packagingId)
    .eq('target_item_id', billingId)
    .eq('relationship_type', 'dependency')
    .limit(1);
  if ((edgeRows?.length ?? 0) > 0) return;

  const workspaceId = getCurrentWorkspaceId() ?? projectId;
  await sb.from('item_relationship_edges').insert({
    id: crypto.randomUUID(),
    workspace_id: workspaceId,
    source_item_id: packagingId,
    target_item_id: billingId,
    relationship_type: 'dependency',
    subtype: 'hard',
    directionality: 'directed',
    status: 'active',
    declared_mode: 'declared',
    rationale: 'Keith commit — packaging blocked by Billing System delivery',
  });
}

const KEITH_TEAM_LABELS: Record<KeithCommitTeam, { capability: string; owner: string }> = {
  marketing: { capability: 'Launch campaign ownership', owner: 'PMM lead' },
  legal: { capability: 'Launch / privacy review', owner: 'Legal counsel' },
  finance: { capability: 'Pricing & revenue approval', owner: 'FP&A partner' },
  support: { capability: 'Launch-ready support coverage', owner: 'CS ops' },
  design: { capability: 'Launch UX / creative', owner: 'Design lead' },
};

function persistEngineInput(source: EngineInputSource): void {
  try {
    sessionStorage.setItem('planning-engine-input', JSON.stringify(source));
  } catch {
    /* noop */
  }
}

async function ensureKeithCommitItems(
  workspaceId: string,
  titles: string[],
): Promise<Map<string, string>> {
  const sb = getSupabaseClient();
  const { data: existing, error } = await sb
    .from('items')
    .select('id, title')
    .eq('workspace_id', workspaceId)
    .in('title', titles);
  if (error) throw error;

  const byTitle = new Map<string, string>();
  for (const row of existing ?? []) {
    if (row.title) byTitle.set(row.title as string, row.id as string);
  }

  const queueId = getRootQueueIdForWorkspace(workspaceId);
  const now = new Date().toISOString();
  const missing = titles.filter((t) => !byTitle.has(t));

  if (missing.length > 0) {
    // Migration 751: the items insert policy requires created_by = auth.uid().
    const { data: authData } = await sb.auth.getUser();
    const userId = authData?.user?.id ?? null;
    if (!userId) throw new Error('You are signed out. Sign in and try again.');
    const inserts = missing.map((title) => {
      const id = crypto.randomUUID();
      byTitle.set(title, id);
      const row: Record<string, unknown> = {
        id,
        workspace_id: workspaceId,
        title,
        description:
          'Keith shared-team commit seed — WP Engine–shaped initiative for cross-functional feasibility.',
        lifecycle_state: 'item',
        lifecycle_stage: 'shaping',
        exit_status: 'active',
        source: 'keith_commit_seed',
        tags: ['keith-commit', 'shared-teams'],
        created_by: userId,
        created_by_user_id: userId,
        created_at: now,
        updated_at: now,
      };
      if (queueId) row.queue_id = queueId;
      return row;
    });
    const { error: insertErr } = await sb.from('items').insert(inserts);
    if (insertErr) throw insertErr;
    dispatchPrioritizationDashboardRefresh({ source: 'keith-commit-seed' });
  }

  return byTitle;
}

async function ensureKeithCommitPool(
  workspaceId: string,
  itemIds: string[],
): Promise<{ poolId: string; created: boolean }> {
  const sb = getSupabaseClient();
  const { data: existing, error } = await sb
    .from('item_pools')
    .select('id, priority_ids')
    .eq('workspace_id', workspaceId)
    .eq('name', KEITH_COMMIT_POOL_NAME)
    .maybeSingle();
  if (error) throw error;

  const now = new Date().toISOString();
  if (existing?.id) {
    const { error: updErr } = await sb
      .from('item_pools')
      .update({ priority_ids: itemIds, updated_at: now })
      .eq('id', existing.id);
    if (updErr) throw updErr;
    await reconcileItemPoolMemberships(existing.id, itemIds, {
      actorId: useUserStore.getState().userId ?? null,
    });
    mirrorKeithPoolInStore(existing.id as string, itemIds, false);
    return { poolId: existing.id as string, created: false };
  }

  const poolId = crypto.randomUUID();
  const ownerId = useUserStore.getState().userId ?? null;
  const { error: insErr } = await sb.from('item_pools').insert({
    id: poolId,
    workspace_id: workspaceId,
    name: KEITH_COMMIT_POOL_NAME,
    description:
      'Keith · shared-team commit — WP Engine–shaped launches for Planning Engine Input Scope.',
    priority_ids: itemIds,
    cut_line_position: 0,
    status: 'draft',
    owner_id: ownerId,
    created_at: now,
    updated_at: now,
  });
  if (insErr) throw insErr;
  await reconcileItemPoolMemberships(poolId, itemIds, { actorId: ownerId });
  mirrorKeithPoolInStore(poolId, itemIds, true);

  return { poolId, created: true };
}

function mirrorKeithPoolInStore(poolId: string, itemIds: string[], isNew: boolean): void {
  try {
    const store = useItemPoolsStore.getState();
    const existing = store.itemPools.find((p) => p.id === poolId);
    if (existing) {
      useItemPoolsStore.setState({
        itemPools: store.itemPools.map((p) =>
          p.id === poolId ? { ...p, priorityIds: itemIds, updatedAt: new Date() } : p,
        ),
      });
      return;
    }
    if (!isNew) return;
    const now = new Date();
    useItemPoolsStore.setState({
      itemPools: [
        ...store.itemPools,
        {
          id: poolId,
          name: KEITH_COMMIT_POOL_NAME,
          description:
            'Keith · shared-team commit — WP Engine–shaped launches for Planning Engine Input Scope.',
          priorityIds: itemIds,
          cutLinePosition: 0,
          status: 'draft',
          isPublished: false,
          version: 0,
          postPublishChanges: [],
          hasUnacknowledgedChanges: false,
          createdAt: now,
          updatedAt: now,
          workspaceId: getCurrentWorkspaceId() ?? undefined,
        } as (typeof store.itemPools)[number],
      ],
    });
  } catch {
    /* Input Scope picker loads from Supabase */
  }
}

/**
 * Create (or refresh) a Keith-only scenario + Item Pool of WP Engine–shaped
 * launches, seed participation / tight PMM+Design supply / funding, place the
 * calendar, and point Planning Engine Input Scope at the pool.
 */
export async function seedKeithSharedTeamCommit(
  projectId: string,
  planningWeeks = 13,
): Promise<{
  scenario: Scenario;
  poolId: string;
  engineInput: EngineInputSource;
  output: TimePhasedRunResult;
  itemIds: string[];
  message: string;
}> {
  const workspaceId = getCurrentWorkspaceId() ?? projectId;
  if (!workspaceId) throw new Error('No workspace in scope for Keith commit seed');

  const existingScenarios = await getScenarios(projectId);
  let scenario = existingScenarios.find((s) => s.name === KEITH_COMMIT_SCENARIO_NAME);
  if (!scenario) {
    scenario = await createScenario(projectId, {
      name: KEITH_COMMIT_SCENARIO_NAME,
      active: false,
      planningMode: 'priority_estimate',
      planningWeeks,
      capacityBufferPercent: 0.3,
      defaultWorkUnitHours: 8,
      includeNotReady: true,
      includeBlocked: false,
      timePhasedEnabled: true,
      notes:
        'Keith shared-team commit — portfolio the whole company can bring to market (PMM/Legal/Finance/CS/Design).',
    });
  } else if (scenario.planningWeeks !== planningWeeks) {
    scenario = await updateScenario(scenario.id, { planningWeeks, timePhasedEnabled: true });
  }
  await activateScenario(scenario.id, projectId);
  scenario = { ...scenario, active: true };

  const titles = KEITH_COMMIT_SPECS.map((s) => s.name);
  const byTitle = await ensureKeithCommitItems(workspaceId, titles);
  const orderedItemIds = titles.map((t) => {
    const id = byTitle.get(t);
    if (!id) throw new Error(`Missing item for ${t}`);
    return id;
  });

  // Ensure taxonomy catalogs exist, then stamp marketing/finance types on Keith items.
  try {
    await getSupabaseClient().rpc('seed_workspace_launch_types', { p_workspace_id: workspaceId });
    await getSupabaseClient().rpc('seed_workspace_investment_types', {
      p_workspace_id: workspaceId,
    });
  } catch (err) {
    console.warn('[keith-commit] taxonomy seed RPCs unavailable (apply migration 626?)', err);
  }
  for (const spec of KEITH_COMMIT_SPECS) {
    const itemId = byTitle.get(spec.name);
    if (!itemId) continue;
    const { error: typeErr } = await getSupabaseClient()
      .from('items')
      .update({
        launch_type: spec.launchType,
        investment_type: spec.investmentType,
        ...(spec.workType ? { work_type: spec.workType } : {}),
      })
      .eq('id', itemId);
    if (typeErr) {
      console.warn(`[keith-commit] could not set types on ${spec.name}`, typeErr.message);
    }
  }

  const { poolId } = await ensureKeithCommitPool(workspaceId, orderedItemIds);
  const engineInput: EngineInputSource = {
    type: 'item_pool',
    id: poolId,
    name: KEITH_COMMIT_POOL_NAME,
  };
  persistEngineInput(engineInput);
  await bindScenarioInputSource(scenario.id, engineInput);

  const horizon = await ensureScenarioHorizon(scenario.id, planningWeeks);
  const profiles = await seedBuiltinLaunchProfiles(projectId);
  const defaultProfileId = profiles[0]?.id;
  if (!defaultProfileId) throw new Error('Could not seed workload templates');

  const demoPriorityIds: { priorityId: string; forecastAmount: number; name?: string }[] = [];
  const stubItems: Item[] = [];

  for (const spec of KEITH_COMMIT_SPECS) {
    const itemId = byTitle.get(spec.name)!;
    const start = addDaysIso(horizon.start, spec.launchOffsetDays);
    const end = addDaysIso(start, spec.windowDays);

    const priority: Priority = {
      id: itemId,
      projectId,
      scenarioId: scenario.id,
      name: spec.name,
      category: null,
      altitudeBand: null,
      track: null,
      itemType: null,
      source: 'keith_commit_seed',
      confidence: null,
      strategicObjective: null,
      accountableLeader: null,
      forceRank: spec.forceRank,
      aboveTheLine: true,
      valueScore: null,
      riskReduction: null,
      complianceFlag: null,
      executiveImperative: null,
      costOfDelay: null,
      impactScore: null,
      effortScore: null,
      cognitiveComplexity: null,
      workDefinitionStatus: 'not_decomposed',
      itemPlanningBasis: 'priority_estimate',
      priorityLevelWuEstimate: spec.effortCu,
      remainingWuEstimate: null,
      priorityResourceType: 'engineering',
      notes: `Keith shared-team commit seed; flexibility:${
        spec.launchType === 'product_launch' ||
        spec.launchType === 'pricing_packaging' ||
        spec.name.includes('Billing System')
          ? 'fixed'
          : 'flexible'
      }`,
      launchProfileId: defaultProfileId,
      targetLaunchStart: start,
      targetLaunchEnd: end,
      owningOrg: spec.owningOrg,
      owningOrgUnitId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await upsertPriorityOverride(priority);
    demoPriorityIds.push({
      priorityId: itemId,
      forecastAmount: spec.forecastAmount,
      name: `${spec.name} cost`,
    });

    stubItems.push({
      id: itemId,
      workspace_id: workspaceId,
      title: spec.name,
      description: null,
      exit_status: 'active',
      altitudeScopingInputs: { workSize: '3-8_weeks' },
      planning_maturity: 'directional',
      itemCategory: 'feature',
      ownerId: null,
      source: 'keith_commit_seed',
      isStrategicallyNominated: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Item);

    const existingTp = await getTeamParticipation(projectId, { priorityId: itemId });
    for (const team of spec.gateTeams) {
      const already = existingTp.some(
        (r) => canonicalizeResourceType(r.requiredTeam) === team,
      );
      if (already) continue;
      const meta = KEITH_TEAM_LABELS[team];
      const deadline = addDaysIso(new Date().toISOString().slice(0, 10), 7);
      const periods = fillPeriodCommitmentsFromWindow(start, end, 'requested', 8);
      // Leave uncommitted so Launch Calendar shows buildable ≠ launchable.
      await createTeamParticipation(projectId, {
        priorityId: itemId,
        requiredTeam: team,
        requiredCapability: meta.capability,
        capacityRequiredWu: team === 'marketing' || team === 'design' ? 4 : 2,
        capacityConfirmed: 'no',
        capabilityPresent: 'yes',
        committed: 'no',
        owner: meta.owner,
        participationDeadline: deadline,
        committedWindowStart: start,
        committedWindowEnd: end,
        requestStatus: 'requested',
        periodCommitments: periods,
        riskIfUnavailable: `Shared ${team} cannot support the launch window`,
        notes: 'Keith commit — leave open to show buildable ≠ launchable',
      });
    }
  }

  const packagingId = byTitle.get(KEITH_PACKAGING_NAME);
  const billingId = byTitle.get(KEITH_BILLING_DELIVERY_NAME);
  if (packagingId && billingId) {
    try {
      await ensureKeithHardDependency(projectId, packagingId, billingId);
    } catch (err) {
      console.warn('[keith-commit] failed to seed hard packaging→billing dependency', err);
    }
  }

  await ensureDefaultSupplyCalendar(scenario.id, horizon.start, horizon.end, 40, projectId);
  const supply = await getSupplyCalendar(scenario.id);
  const tightTypes = new Set(['marketing', 'design']);
  const tightRows = supply
    .filter((r) => tightTypes.has(canonicalizeResourceType(r.resourceType)))
    .map((r) => ({
      scenarioId: scenario.id,
      resourceType: r.resourceType,
      resourceName: r.resourceName,
      bucketStart: r.bucketStart,
      bucketEnd: r.bucketEnd,
      supplyHours: 8,
    }));
  if (tightRows.length > 0) await upsertSupplyCalendarRows(tightRows);

  // Viability needs engineering headroom so launches are buildable; PMM/Design stay
  // tight so the calendar shows shared-team overload + buildable ≠ launchable.
  const capacityTargets: Array<{ type: string; label: string; hours: number; notes: string }> = [
    {
      type: 'engineering',
      label: 'Engineering',
      hours: 120,
      notes: 'Keith commit — eng can build; shared teams are the constraint',
    },
    {
      type: 'marketing',
      label: 'Product Marketing',
      hours: 8,
      notes: 'Keith commit — constrained Product Marketing',
    },
    {
      type: 'design',
      label: 'Experience Design',
      hours: 8,
      notes: 'Keith commit — constrained Experience Design',
    },
  ];
  const capacity = await getCapacityRows(projectId, scenario.id);
  for (const target of capacityTargets) {
    if (capacity.some((c) => canonicalizeResourceType(c.resourceType) === target.type)) continue;
    try {
      await createCapacityRow(projectId, {
        scenarioId: scenario.id,
        resourceType: target.type,
        resourceName: target.label,
        weeklyCapacityHours: target.hours,
        notes: target.notes,
      });
    } catch {
      /* supply calendar still drives placement */
    }
  }

  await seedDemoFinancialLayer({
    projectId,
    scenarioId: scenario.id,
    horizonStart: horizon.start,
    horizonEnd: horizon.end,
    prioritySpecs: demoPriorityIds,
    weeklyApproved: 14_000,
    envelopeName: 'Keith shared-team funding',
  });

  const output = await runTimePhasedPlacement(projectId, scenario.id);

  let blockedByDep = 0;
  try {
    const viability = await runEngineScoped({
      projectId,
      scenario,
      items: stubItems,
      edges: [],
      scopedItemIds: new Set(orderedItemIds),
    });
    blockedByDep = viability.priorityPlanRecords.filter((r) => r.planDecision === 'Blocked').length;
    await upsertPlanningRun({
      projectId,
      scenarioId: scenario.id,
      engineInput,
      scopedItemIds: orderedItemIds,
      output: viability,
    });
  } catch (err) {
    console.warn('[keith-commit] failed to persist pool-scoped viability', err);
  }

  const bnL = output.placements.filter((p) => p.buildable && !p.launchable).length;
  const moneyNote = output.financial?.affordability?.withinEnvelope
    ? 'funding within envelope'
    : `funding overage $${Math.round(output.financial?.affordability?.overageTotal ?? 0).toLocaleString()}`;
  const message =
    `Keith shared-team commit ready: scenario “${KEITH_COMMIT_SCENARIO_NAME}”, ` +
    `Item Pool “${KEITH_COMMIT_POOL_NAME}” (${orderedItemIds.length} launches). ` +
    `${output.summary.total} placed (${bnL} buildable ≠ launchable). ` +
    (blockedByDep > 0
      ? `${blockedByDep} initiative${blockedByDep !== 1 ? 's' : ''} Blocked by hard deps (packaging → billing). `
      : '') +
    `PMM + Design supply 8h/week. Money: ${moneyNote}. ` +
    `Planning Engine Input Scope is set to the pool — Run stays on this subset.`;

  return {
    scenario,
    poolId,
    engineInput,
    output,
    itemIds: orderedItemIds,
    message,
  };
}

const SAMPLE_PORTFOLIO_PREFIX = '[Sample portfolio]';

/**
 * Structural sample from a prior-company product-priorities workbook (not WP Engine).
 * Multiple ranked org portfolios + constrained Experience Design supply — same shape
 * as Keith’s shared-team overload, with UX/Design as the contended pool.
 */
export async function seedSamplePortfolio(
  projectId: string,
  scenarioId: string,
  planningWeeks: number,
): Promise<{
  output: TimePhasedRunResult;
  createdPriorityIds: string[];
  message: string;
}> {
  const horizon = await ensureScenarioHorizon(scenarioId, planningWeeks);
  const profiles = await seedBuiltinLaunchProfiles(projectId);
  const defaultProfileId = profiles[0]?.id;
  if (!defaultProfileId) throw new Error('Could not seed workload templates');

  const existing = (await getPriorities(projectId)).filter(
    (p) =>
      (p.scenarioId === scenarioId || !p.scenarioId) &&
      p.name.startsWith(SAMPLE_PORTFOLIO_PREFIX),
  );

  /** Curated from Product_Priorities_v17 — names kept for recognizability; not live terms. */
  const demoSpecs = [
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} Cloud on Bare Metal`,
      forceRank: 1,
      effortCu: 20,
      launchOffsetDays: 56,
      windowDays: 21,
      owningOrg: 'Infrastructure',
      needDesign: true,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} Dedicated vCloud Director`,
      forceRank: 2,
      effortCu: 32,
      launchOffsetDays: 63,
      windowDays: 21,
      owningOrg: 'Custom Dedicated',
      needDesign: true,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} Icehouse upgrade`,
      forceRank: 3,
      effortCu: 16,
      launchOffsetDays: 84,
      windowDays: 28,
      owningOrg: 'Private Cloud',
      needDesign: false,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} SS50 Release 1`,
      forceRank: 4,
      effortCu: 24,
      launchOffsetDays: 49,
      windowDays: 14,
      owningOrg: 'Platform',
      needDesign: true,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} Emergency website changes`,
      forceRank: 5,
      effortCu: 8,
      launchOffsetDays: 42,
      windowDays: 14,
      owningOrg: 'Platform',
      needDesign: true,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} New website (Vermeer)`,
      forceRank: 6,
      effortCu: 28,
      launchOffsetDays: 98,
      windowDays: 28,
      owningOrg: 'Platform',
      needDesign: true,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} Self Service 50`,
      forceRank: 7,
      effortCu: 20,
      launchOffsetDays: 70,
      windowDays: 21,
      owningOrg: 'Experience Design',
      needDesign: true,
    },
    {
      name: `${SAMPLE_PORTFOLIO_PREFIX} Hybrid Customer Model`,
      forceRank: 8,
      effortCu: 12,
      launchOffsetDays: 91,
      windowDays: 21,
      owningOrg: 'Experience Design',
      needDesign: true,
    },
  ];

  const createdPriorityIds: string[] = [];
  const demoPriorityIds: { priorityId: string; forecastAmount: number; name?: string }[] = [];
  const byName = new Map(existing.map((p) => [p.name, p]));

  for (const spec of demoSpecs) {
    let priority = byName.get(spec.name);
    const start = addDaysIso(horizon.start, spec.launchOffsetDays);
    const end = addDaysIso(start, spec.windowDays);
    if (!priority) {
      priority = await createPriority(projectId, {
        scenarioId,
        name: spec.name,
        forceRank: spec.forceRank,
        priorityLevelWuEstimate: spec.effortCu,
        launchProfileId: defaultProfileId,
        targetLaunchStart: start,
        targetLaunchEnd: end,
        owningOrg: spec.owningOrg,
        aboveTheLine: true,
      });
      createdPriorityIds.push(priority.id);
    } else {
      await updatePriority(priority.id, {
        forceRank: spec.forceRank,
        priorityLevelWuEstimate: spec.effortCu,
        launchProfileId: defaultProfileId,
        targetLaunchStart: start,
        targetLaunchEnd: end,
        owningOrg: spec.owningOrg,
      });
    }
    demoPriorityIds.push({
      priorityId: priority.id,
      forecastAmount: spec.effortCu * 6000,
      name: `${spec.name} cost`,
    });

    if (spec.needDesign) {
      const rows = await getTeamParticipation(projectId, { priorityId: priority.id });
      const hasDesign = rows.some((r) => isLaunchGateTeam(r.requiredTeam));
      if (!hasDesign) {
        const deadline = addDaysIso(new Date().toISOString().slice(0, 10), 7);
        const periods = fillPeriodCommitmentsFromWindow(start, end, 'requested', 8);
        await createTeamParticipation(projectId, {
          priorityId: priority.id,
          requiredTeam: 'design',
          requiredCapability: 'Experience design / launch UX',
          capacityRequiredWu: 4,
          capacityConfirmed: 'no',
          capabilityPresent: 'yes',
          committed: 'no',
          owner: 'Experience Design lead',
          participationDeadline: deadline,
          committedWindowStart: start,
          committedWindowEnd: end,
          requestStatus: 'requested',
          periodCommitments: periods,
          riskIfUnavailable: 'Cannot ship customer-facing launch on target date without UX',
          notes: 'Sample portfolio — leave uncommitted to show buildable ≠ launchable',
        });
      }
    }
  }

  // Tight design supply (~8h/week) — Experience Design as the contended shared pool.
  await ensureDefaultSupplyCalendar(scenarioId, horizon.start, horizon.end, 40, projectId);
  const supply = await getSupplyCalendar(scenarioId);
  const designRows = supply
    .filter((r) => canonicalizeResourceType(r.resourceType) === 'design')
    .map((r) => ({
      scenarioId,
      resourceType: r.resourceType,
      resourceName: r.resourceName ?? 'Experience Design',
      bucketStart: r.bucketStart,
      bucketEnd: r.bucketEnd,
      supplyHours: 8,
    }));
  if (designRows.length > 0) {
    await upsertSupplyCalendarRows(designRows);
  }

  const capacity = await getCapacityRows(projectId, scenarioId);
  const designCap = capacity.find((c) => canonicalizeResourceType(c.resourceType) === 'design');
  if (!designCap) {
    try {
      await createCapacityRow(projectId, {
        scenarioId,
        resourceType: 'design',
        resourceName: 'Experience Design',
        weeklyCapacityHours: 8,
        notes: 'Sample portfolio — constrained UX/Design',
      });
    } catch {
      // Optional — supply calendar still drives placement.
    }
  }

  await seedDemoFinancialLayer({
    projectId,
    scenarioId,
    horizonStart: horizon.start,
    horizonEnd: horizon.end,
    prioritySpecs: demoPriorityIds,
    weeklyApproved: 25000,
    envelopeName: 'Sample portfolio funding',
  });

  const output = await runTimePhasedPlacement(projectId, scenarioId);
  await persistLiveScopeViabilityInstance(projectId, scenarioId);
  const bnL = output.placements.filter((p) => p.buildable && !p.launchable).length;
  const moneyNote = output.financial?.affordability?.withinEnvelope
    ? 'funding within envelope'
    : `funding overage $${Math.round(output.financial?.affordability?.overageTotal ?? 0).toLocaleString()}`;
  const message =
    `Sample portfolio ready: ${output.summary.total} launches from a prior-company workbook ` +
    `(not WP Engine). ${output.summary.slipped} slipped, ${bnL} buildable but not launchable. ` +
    `Design supply is 8h/week; several priorities await Experience Design participation. ` +
    `Money: ${moneyNote}.`;

  return { output, createdPriorityIds, message };
}

/**
 * Read each published list in a roll-up, in source order, with its items in
 * list order. The merge (`applyPortfolioRollupSources`) and the conflict map
 * (`findRollupConflicts`) both read lists through this, so they always agree.
 */
export async function resolvePortfolioRollupLists(sources: PortfolioRollupSource[]): Promise<{
  lists: { source: PortfolioRollupSource; items: { id: string; title: string; effortCu?: number }[] }[];
  unmatchedLists: string[];
}> {
  const lists: { source: PortfolioRollupSource; items: { id: string; title: string; effortCu?: number }[] }[] = [];
  const unmatchedLists: string[] = [];
  for (const source of sources) {
    if (source.sourceType !== 'published_priority_list') continue;
    const resolved = await resolveInputItemIds('published_priority_list', source.sourceReferenceId);
    if (resolved.error || resolved.itemIds.length === 0) {
      unmatchedLists.push(source.sourceLabel ?? source.sourceReferenceId);
      continue;
    }
    const { data: rows } = await getSupabaseClient()
      .from('items')
      .select('id, title, effort_capacity_units')
      .in('id', resolved.itemIds);
    const byId = new Map((rows ?? []).map((row) => [row.id as string, row]));
    const items: { id: string; title: string; effortCu?: number }[] = [];
    for (const id of resolved.itemIds) {
      const row = byId.get(id);
      if (!row) continue;
      items.push({
        id,
        title: (row.title as string) || 'Untitled',
        effortCu: row.effort_capacity_units != null ? Number(row.effort_capacity_units) : undefined,
      });
    }
    lists.push({ source, items });
  }
  return { lists, unmatchedLists };
}

/**
 * Materialize saved portfolio rollup sources into planning priorities for the
 * scenario — resolve published lists → item titles → create/update priorities.
 */
export async function applyPortfolioRollupSources(
  projectId: string,
  scenarioId: string,
): Promise<{ created: number; updated: number; itemCount: number; unmatchedLists: string[] }> {
  const sources = await getPortfolioRollupSources(scenarioId);
  if (!sources.length) {
    return { created: 0, updated: 0, itemCount: 0, unmatchedLists: [] };
  }

  const existing = (await getPriorities(projectId)).filter(
    (p) => p.scenarioId === scenarioId || !p.scenarioId,
  );
  const byName = new Map(existing.map((p) => [p.name.trim().toLowerCase(), p]));

  const { lists, unmatchedLists } = await resolvePortfolioRollupLists(sources);

  let created = 0;
  let updated = 0;
  let itemCount = 0;
  let forceRank = 1;

  for (const { source, items } of lists) {
    for (const item of items) {
      itemCount += 1;
      const key = item.title.trim().toLowerCase();
      const found = byName.get(key);
      if (found) {
        await updatePriority(found.id, {
          scenarioId,
          forceRank,
          owningOrg: source.owningOrg ?? found.owningOrg,
          owningOrgUnitId: source.owningOrgUnitId ?? found.owningOrgUnitId,
          ...(item.effortCu != null && item.effortCu > 0
            ? { priorityLevelWuEstimate: item.effortCu }
            : {}),
        });
        updated += 1;
      } else {
        const createdPri = await createPriority(projectId, {
          scenarioId,
          name: item.title,
          forceRank,
          owningOrg: source.owningOrg ?? null,
          owningOrgUnitId: source.owningOrgUnitId ?? null,
          priorityLevelWuEstimate: item.effortCu ?? null,
          aboveTheLine: true,
        });
        byName.set(key, createdPri);
        created += 1;
      }
      forceRank += 1;
    }
  }

  return { created, updated, itemCount, unmatchedLists };
}

/** Convenience: save sources then materialize priorities in one call. */
export async function saveAndApplyPortfolioRollup(
  projectId: string,
  scenarioId: string,
  sources: Omit<PortfolioRollupSource, 'id'>[],
): Promise<Awaited<ReturnType<typeof applyPortfolioRollupSources>>> {
  await savePortfolioRollupSources(scenarioId, sources);
  return applyPortfolioRollupSources(projectId, scenarioId);
}
